import "swiper/css";

import { Dialog } from "@headlessui/react";
import classNames from "classnames";
import { AnimatePresence, motion, useMotionValue } from "framer-motion";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { Dispatch, memo, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import SwiperCore, { Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { useBreakpoint } from "~/contexts/breakpoint";
import { useGiftCardMedia } from "~/contexts/giftcard-media";
import CrossCountry from "~/types/cross-country";
import { basicAnimationStates } from "~/utils/animation-utils";
import Constants from "~/utils/constants";
import imageUtils from "~/utils/image-utils";

import Icon from "../common/icon";
import NextImage from "../common/next-image";
import { CarouselArrows, updateArrowStatus } from "../editorials/carousel-utils/carousel-utils";
import RevieveModalTrigger from "../editorials/revieve-modal-trigger/revieve-modal-trigger";
import styles from "./product-gallery.module.scss";
import ProductGalleryNavigator from "./product-gallery-navigator";

type Props = {
  selectedShade: CrossCountry.DetailedProduct;
  galleryIdx: number;
  onSelect: (idx: number) => void;
  enabledGallery: boolean;
  setEnabledGallery: Dispatch<SetStateAction<boolean>>;
};

function getGalleryObjectsData(product: Props["selectedShade"]) {
  const order = Constants.PRODUCT_MEDIA_GALLERY_ORDER;
  const gallery = product?.product_media?.gallery ?? [];
  const media = product?.product_media?.media ?? [];

  return media
    .filter((m) => {
      if (!gallery.includes(m.blob)) return false;
      if (!order.includes(m.name)) return false;
      return true;
    })
    .sort((a, b) => {
      const aSuffixIdx = order.indexOf(a.name);
      const bSuffixIdx = order.indexOf(b.name);

      return aSuffixIdx - bSuffixIdx;
    });
}

export const RevieveTarget = memo(function RevieveTarget() {
  return <div id="revieve-target"></div>;
});

export default function ProductGallery(props: Props) {
  const t = useTranslations();

  const [swiperInstance, setSwiperInstance] = useState<SwiperCore>();

  const router = useRouter();

  const { mediaUrl: giftcardMediaUrl } = useGiftCardMedia();
  const breakpoint = useBreakpoint();
  const media = getGalleryObjectsData(props.selectedShade);
  const assets = giftcardMediaUrl ? [{ id: props.selectedShade.product_id, url: giftcardMediaUrl }] : media;

  const [openGallery, setOpenGallery] = useState(false);
  const [activateCursor, setActivateCursor] = useState(false);
  const cursorX = useMotionValue(0);
  const cursorY = useMotionValue(0);

  const [arrowsStatus, setArrowsStatus] = useState({ arrowL: false, arrowR: false });
  const [path, setPath] = useState(router.asPath);
  const [firstIndex, setFirstIndex] = useState(props.galleryIdx);

  useEffect(() => {
    if (router.asPath !== path) {
      setFirstIndex(props.galleryIdx);
      setPath(router.asPath);
    }
    if (firstIndex > assets.length - 1) {
      setFirstIndex(assets.length - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets.length, router.asPath]);

  useEffect(() => {
    if (swiperInstance) {
      setArrowsStatus(updateArrowStatus(swiperInstance));
    }
  }, [swiperInstance]);

  useEffect(() => {
    const moveCursor = (e: MouseEvent) => {
      cursorX.set(e.clientX - 16);
      cursorY.set(e.clientY - 16);
    };

    if (activateCursor) {
      window.addEventListener("mousemove", moveCursor);
    }

    return () => {
      window.removeEventListener("mousemove", moveCursor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activateCursor]);

  useEffect(() => {
    if (swiperInstance && !swiperInstance?.destroyed) {
      swiperInstance.slideTo(props.galleryIdx);
    }
  }, [swiperInstance, props.galleryIdx]);

  useEffect(() => {
    if (swiperInstance && !swiperInstance?.destroyed) {
      if (breakpoint == "mobile" || openGallery) {
        swiperInstance.allowTouchMove = true;
      } else {
        swiperInstance.allowTouchMove = false;
      }
    }
  }, [swiperInstance, breakpoint, openGallery]);

  const [isZoomedIn, setIsZoomedIn] = useState(false);
  const figureDesktopRef = useRef<HTMLElement[]>([]);

  const swiperWrapper = (
    <Swiper
      modules={[Pagination]}
      pagination={
        !openGallery && media.length > 1
          ? {
              clickable: true,
              bulletActiveClass: styles.bulletActive,
              bulletClass: styles.bulletClass,
              horizontalClass: styles.horizontalClass,
              renderBullet: function (i, className) {
                return `<button class="${className}" aria-label="${t.rich("a11y.goToSlide", {
                  number: i + 1,
                })}" />`;
              },
            }
          : false
      }
      breakpoints={{
        900: {
          direction: openGallery ? "horizontal" : "vertical",
          pagination: false,
        },
      }}
      allowSlideNext={!isZoomedIn}
      allowSlidePrev={!isZoomedIn}
      onSwiper={setSwiperInstance}
      onSlideChange={(swiper) => props.onSelect(swiper.activeIndex)}
      onTransitionEnd={(currentSwiper) => setArrowsStatus(updateArrowStatus(currentSwiper))}
      direction="horizontal"
      className={classNames(
        styles.galleryCarousel,
        !swiperInstance && media.length > 1 ? styles.initialSpaceForBullets : undefined
      )}
      initialSlide={props.galleryIdx}
      speed={Constants.SWIPER_TRANSITION_DURATION}
    >
      {assets.map((m, index) => (
        <SwiperSlide key={m.id} className={styles.gallerySlide} aria-hidden={swiperInstance?.activeIndex != index}>
          <button
            className={classNames(styles.galleryButton, styles.galleryMobile)}
            aria-label={t("a11y.galleryButton")}
            onClick={() => {
              setOpenGallery(true);
              setIsZoomedIn(false);
              setActivateCursor(false);
            }}
            tabIndex={swiperInstance?.activeIndex != index || openGallery ? -1 : 0}
          >
            <figure className={classNames(styles.galleryImgWrapper, { [styles.whileOpen]: openGallery })}>
              <TransformWrapper
                disablePadding
                onTransformed={(e) => setIsZoomedIn(e.state.scale !== 1)}
                doubleClick={{ mode: isZoomedIn ? "reset" : "zoomIn" }}
                wheel={{ disabled: true }}
                panning={{ disabled: !isZoomedIn }}
              >
                <TransformComponent
                  wrapperStyle={{ width: "100%", height: "100%" }}
                  contentStyle={{ width: "100%", height: "100%" }}
                >
                  <NextImage
                    priority={index === firstIndex}
                    className={styles.galleryImage}
                    alt={props.selectedShade.display_value}
                    src={m.url}
                    sizes={`(min-width: 900px) 0vw, ${openGallery ? "200vw" : "92vw"}`}
                    quality={80}
                    fill
                    // Using imageUtils.evaImageLoader causes the image to be placed in the center but
                    // not stretched, resulting in an image that looks smaller.
                    // See ECO-342
                    loader={giftcardMediaUrl ? imageUtils.contentfulImageLoader : undefined}
                  />
                </TransformComponent>
              </TransformWrapper>
            </figure>
          </button>
          <button
            onMouseEnter={() => setActivateCursor(true)}
            onMouseLeave={() => setActivateCursor(false)}
            className={classNames(
              styles.galleryButton,
              styles.galleryDesktop,
              openGallery ? styles.galleryButtonOpen : undefined
            )}
            onClick={() => {
              setOpenGallery(true);
              setIsZoomedIn(false);
              setActivateCursor(false);
            }}
            disabled={!props.enabledGallery}
            aria-label={!openGallery ? t("a11y.galleryButton") : ""}
            tabIndex={swiperInstance?.activeIndex != index || openGallery ? -1 : 0}
          >
            <figure
              className={styles.galleryImgWrapper}
              onMouseMove={(e) => {
                if (!openGallery) {
                  const rect = figureDesktopRef.current[index].parentElement!.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const y = e.clientY - rect.top;
                  figureDesktopRef.current[index].style.transformOrigin = `${x}px ${y}px`;
                  figureDesktopRef.current[index].style.transform = "scale(1.5)";
                }
              }}
              onMouseLeave={() => {
                figureDesktopRef.current[index].style.transformOrigin = `center center`;
                figureDesktopRef.current[index].style.transform = "scale(1)";
              }}
            >
              <NextImage
                priority={index === firstIndex}
                className={styles.galleryImage}
                alt={props.selectedShade.display_value}
                src={m.url}
                fill
                ref={(ref) => {
                  if (figureDesktopRef.current && ref) figureDesktopRef.current[index] = ref;
                }}
                sizes="(min-width: 900px) 60vw, 0vw"
                quality={80}
                // Using imageUtils.evaImageLoader causes the image to be placed in the center but
                // not stretched, resulting in an image that looks smaller.
                // See ECO-342
                loader={giftcardMediaUrl ? imageUtils.contentfulImageLoader : undefined}
              />
            </figure>
          </button>
        </SwiperSlide>
      ))}
    </Swiper>
  );

  return (
    <div className={styles.pdpGallery}>
      <RevieveTarget />
      <AnimatePresence>
        {openGallery && (
          <Dialog
            as={motion.div}
            initial={basicAnimationStates.off}
            animate={basicAnimationStates.on}
            exit={basicAnimationStates.off}
            open={openGallery}
            onClose={() => setOpenGallery(false)}
            className={styles.galleryDialog}
          >
            <Dialog.Panel className={styles.galleryPanel}>
              <button
                aria-label={t("generic.close")}
                className={styles.galleryPanelClose}
                onClick={() => setOpenGallery(false)}
              >
                <Icon name="close" height={24} width={24} aria-hidden />
              </button>
              {swiperWrapper}
              {swiperInstance && (
                <CarouselArrows
                  swiperInstance={swiperInstance}
                  arrowsStatus={arrowsStatus}
                  className={styles.galleryArrows}
                />
              )}
              <motion.div
                initial={basicAnimationStates.on}
                animate={isZoomedIn ? basicAnimationStates.off : basicAnimationStates.on}
                exit={isZoomedIn ? basicAnimationStates.on : basicAnimationStates.off}
              >
                <ProductGalleryNavigator
                  selectedShade={props.selectedShade}
                  galleryIdx={props.galleryIdx}
                  onSelect={props.onSelect}
                  className={styles.galleryNav}
                  enabledGallery
                />
              </motion.div>
            </Dialog.Panel>
          </Dialog>
        )}
      </AnimatePresence>
      {!openGallery && (
        <motion.div className={styles.galleryWrapper}>
          <motion.div
            animate={activateCursor ? basicAnimationStates.on : basicAnimationStates.off}
            className={styles.cursor}
            style={{ x: cursorX, y: cursorY }}
          >
            <Icon name={"zoom-in"} aria-hidden />
          </motion.div>
          {swiperWrapper}
        </motion.div>
      )}
      {/* open gallery & vto button mobile */}
      <div className={styles.wrapperRevieveTrigger}>
        <button
          className={styles.openGallery}
          onClick={() => {
            setOpenGallery(true);
            setIsZoomedIn(false);
          }}
        >
          <Icon name="zoom-in" aria-hidden />
        </button>
        <RevieveModalTrigger
          revieveType="PDPVirtualTryOn"
          className={styles.bigButton}
          variant="serviceMedium"
          buttonText={t("generic.tryitnow")}
          productId={props.selectedShade.backend_id}
          renderToId="revieve-target"
          hidden={!props.selectedShade.try_it_on}
          onOpen={() => props.setEnabledGallery(false)}
          onClose={() => props.setEnabledGallery(true)}
        />
      </div>
    </div>
  );
}
